import { useMemo, useState } from 'react';
import { SideDrawer } from '@/components/ui/SideDrawer';
import { Skeleton } from '@/components/ui/Skeleton';
import { AppRecordFields } from '@/components/apps/AppRecordFields';
import { AppRecordsTable } from '@/components/apps/AppRecordsTable';
import { ObjectBadge } from '@/components/apps/CellValue';
import { StatusPicker } from '@/components/apps/StatusPicker';
import { columnFormats, listColumns, recordTitle } from '@/components/apps/app-records-model';
import type { AppCollection, AppRecord } from '@/lib/app-records';

export interface PreviewNextAction {
  title: string;
  whyNow: string | null;
}

/** By test row id: the planned actions, or null when the planner could not answer. */
export type PreviewNextActions = Record<string, PreviewNextAction[] | null>;

interface ForgeAppPreviewProps {
  collection: AppCollection;
  records: AppRecord[];
  /** U1: next actions of the first objects; 'loading' while Domino plans them. */
  nextActions: PreviewNextActions | 'loading' | null;
}

/**
 * U1 (02/11/2026): step ② of the Forge shows the app as the team will open it,
 * filled with the test's objects: its list, each object's page, Domino's next
 * actions on the first ones. Statuses can be tried; nothing is saved.
 */
export const ForgeAppPreview = ({ collection, records: initial, nextActions }: ForgeAppPreviewProps) => {
  const [records, setRecords] = useState(initial);
  const [openId, setOpenId] = useState<string | null>(null);
  const formats = useMemo(() => columnFormats(collection, records), [collection, records]);
  const open = records.find((record) => record.id === openId) ?? null;
  const planned = nextActions && nextActions !== 'loading' ? nextActions : null;
  const actionsOf = (record: AppRecord) => planned?.[record.recordKey] ?? null;
  const firstActions = planned ? Object.fromEntries(records.flatMap((record) => {
    const actions = actionsOf(record);
    return actions && actions[0] ? [[record.id, { title: actions[0].title, count: actions.length }]] : [];
  })) : null;
  const setStatus = (record: AppRecord, status: string) =>
    setRecords((current) => current.map((item) => (item.id === record.id ? { ...item, status } : item)));

  return (
    <section aria-label="Aperçu de l’app" className="space-y-3">
      <h2 className="font-display text-lg font-semibold text-domino-ink">{collection.title}</h2>
      <AppRecordsTable collection={collection} records={records} members={[]} selectedId={openId}
        onSelect={(record) => setOpenId(record.id)} onStatus={setStatus} nextActions={firstActions} />
      {open && (
        <SideDrawer label="Fiche" title={recordTitle(open, collection)} onClose={() => setOpenId(null)}>
          <div className="space-y-5 px-5 py-6 lg:px-8">
            <div className="flex items-center gap-3">
              {(collection.objectKind === 'company' || collection.objectKind === 'person') && <ObjectBadge name={recordTitle(open, collection)} size="lg" />}
              <StatusPicker statuses={collection.statuses} value={open.status} onChange={(status) => setStatus(open, status)} />
            </div>
            {nextActions === 'loading' && records.indexOf(open) < 3 && (
              <div className="space-y-2" aria-busy="true"><Skeleton className="h-4 w-2/3" /><Skeleton className="h-3 w-5/6" /></div>
            )}
            {(actionsOf(open)?.length ?? 0) > 0 && (
              <ol className="space-y-2">
                {actionsOf(open)!.map((action) => (
                  <li key={action.title} className="rounded-xl border border-domino-cardBorder bg-domino-warm px-4 py-3">
                    <p className="text-sm font-semibold text-domino-ink">{action.title}</p>
                    {action.whyNow && <p className="mt-0.5 text-sm text-domino-inkSoft">{action.whyNow}</p>}
                  </li>
                ))}
              </ol>
            )}
            <AppRecordFields record={open} collection={collection} formats={formats}
              deciding={listColumns(collection, undefined, formats).map((column) => column.key)} />
          </div>
        </SideDrawer>
      )}
    </section>
  );
};
